'use client'
// LOCKED-SCREEN email login (2026-07-18): the interactive "log in with this
// message's email" option shown BELOW the static password form on a locked
// desk. It lives here (app/visitor/ui — the auth UI home, styled, outside the
// deskUi no-CSS scan) and is rendered by the skeleton LockedScreen, which only
// passes the codekey.
//
// Two steps, no captcha (the send is message-scoped server-side — see
// send_msg_login_code): (1) email → apiSendMsgLoginCode mails a one-time code
// ONLY if the address is this message's own login email, and always answers
// opaquely; (2) the 5-cell code (same UX as the front page) → the ordinary
// email login (visitor.enterWithEmail) → the provider reloads the desk, and
// logging in as the message auto-unlocks it (owner possession).

import React, { useContext, useRef, useState } from 'react'
import type { ILoginEmail } from 'msgdsk-types'
import { AppContext } from '@context/AppContext'
import { apiSendMsgLoginCode } from '@libs/apiClient'
import { checkEmail } from '@libs/utilsMsg'
import { useVisitor } from '../useVisitor'

const CELLS = [0, 1, 2, 3, 4]

export function LockedEmailLogin({ codeKey }: { codeKey: string }) {
  const { context_data } = useContext(AppContext)
  const visitor = useVisitor()
  const url = context_data.origin?.[3] ?? ''

  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [msgId, setMsgId] = useState('')
  const [code, setCode] = useState(['', '', '', '', ''])
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const cellRefs = useRef<Array<HTMLInputElement | null>>([])
  const submittingRef = useRef(false)

  const send = async () => {
    if (busy) return
    if (!checkEmail(email)) { setError('Enter a valid email.'); return }
    setBusy(true); setError('')
    const result = await apiSendMsgLoginCode(codeKey, email.trim(), url)
    setBusy(false)
    // Opaque either way: advance to the code step regardless, so the form
    // never reveals whether the address is this message's login email.
    setMsgId(result?.msg_id ?? '')
    setCode(['', '', '', '', ''])
    setStep('code')
    setNote('If that address belongs to this message, a sign-in code is on its way.')
    window.setTimeout(() => cellRefs.current[0]?.focus(), 0)
  }

  const verify = async (full: string) => {
    if (submittingRef.current || !msgId) return
    submittingRef.current = true
    setBusy(true); setError('')
    try {
      const login = { domain: url, msg_id: msgId, email: email.trim(), pin: full } as unknown as ILoginEmail
      const entered = await visitor.enterWithEmail(login)
      if (!entered) {
        setCode(['', '', '', '', ''])
        setError('That code did not work — check it and try again.')
        window.setTimeout(() => cellRefs.current[0]?.focus(), 0)
      }
      // On success the provider reloads the desk (possession); nothing to do.
    } finally {
      submittingRef.current = false
      setBusy(false)
    }
  }

  const onCell = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '')
    if (digits === '') {
      const next = [...code]; next[index] = ''; setCode(next); return
    }
    const next = [...code]
    let i = index
    for (const d of digits) { if (i > 4) break; next[i] = d; i += 1 }
    setCode(next)
    if (next.every(v => v.length === 1)) { void verify(next.join('')); return }
    cellRefs.current[Math.min(i, 4)]?.focus()
  }

  const onCellKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) cellRefs.current[index - 1]?.focus()
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--content-locked-input-bg, #f3f4f6)',
    border: '1px solid var(--content-locked-input-border, #d7dbe0)',
    borderRadius: 'var(--content-locked-input-radius, 4px)',
    color: 'inherit',
  }

  if (!open) {
    return (
      <div className="md-locked-email mt-2 text-center text-[12px]">
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 underline decoration-dotted underline-offset-2"
          style={{ color: 'var(--content-locked-muted, #6b7280)' }}
          onClick={() => setOpen(true)}
        >
          Or sign in with this message's email
        </button>
      </div>
    )
  }

  return (
    <div className="md-locked-email mx-auto mt-3 flex w-full max-w-[280px] flex-col items-stretch gap-2 text-[13px]">
      {step === 'email' ? (
        <>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Your email"
            value={email}
            disabled={busy}
            onChange={e => { setEmail(e.target.value); if (error) setError('') }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void send() } }}
            className="px-3 py-2 text-center outline-none"
            style={inputStyle}
          />
          <button
            type="button"
            className="cursor-pointer px-3 py-2 text-[13px] font-semibold disabled:opacity-60"
            style={{ background: 'var(--content-locked-button-bg, #e5e7eb)', border: '1px solid var(--content-locked-input-border, #d7dbe0)', borderRadius: 'var(--content-locked-input-radius, 4px)', color: 'var(--content-locked-fg, #2b3440)' }}
            disabled={busy}
            onClick={() => void send()}
          >
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
        </>
      ) : (
        <>
          {note ? <div className="text-[12px]" style={{ color: 'var(--content-locked-muted, #6b7280)' }}>{note}</div> : null}
          <div className="flex justify-center gap-1.5">
            {CELLS.map(i => (
              <input
                key={i}
                ref={el => { cellRefs.current[i] = el }}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={code[i]}
                disabled={busy}
                onChange={e => onCell(i, e.target.value)}
                onKeyDown={e => onCellKeyDown(i, e)}
                className="h-10 w-9 text-center text-[16px] outline-none"
                style={inputStyle}
              />
            ))}
          </div>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-[11px] underline decoration-dotted underline-offset-2"
            style={{ color: 'var(--content-locked-faint, #9aa3ad)' }}
            onClick={() => { setStep('email'); setError(''); setNote('') }}
          >
            Use a different email
          </button>
        </>
      )}
      {error ? <div className="text-[12px] text-[#b4433a]">{error}</div> : null}
    </div>
  )
}
